import { initializeApp, deleteApp } from 'firebase/app';
import { createUserWithEmailAndPassword, getAuth, signOut } from 'firebase/auth';
import {
  Timestamp,
  collection,
  doc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import { db, firebaseConfig } from '../firebase/config';
import { supabase } from '../lib/supabase';
import { AccessPermission } from '../types/adminAccess';
import { normalizeAssignedRegions } from '../utils/regionAccess';

export type OrganizationRole = 'superAdmin' | 'admin' | 'employee';

export interface OrganizationMember {
  uid: string;
  email: string;
  displayName: string;
  role: OrganizationRole;
  access: AccessPermission[];
  active: boolean;
  regions?: string[];
  inviteStatus: 'pending' | 'active';
  createdAt?: Timestamp | Date | null;
  updatedAt?: Timestamp | Date | null;
  createdByUid?: string;
  provisioningMode?: 'created' | 'upgraded';
}

export interface CreateOrganizationMemberInput {
  email: string;
  displayName: string;
  password: string;
  role: Exclude<OrganizationRole, 'superAdmin'>;
  access: AccessPermission[];
  regions?: string[];
  createdByUid: string;
}

class OrganizationManagementService {
  private normalizeMember(record: any): OrganizationMember {
    const data = record.data();

    return {
      uid: record.id,
      email: data.email || '',
      displayName: data.displayName || data.display_name || '',
      role: data.role || 'admin',
      access: Array.isArray(data.access)
        ? data.access
        : data.role === 'superAdmin'
          ? ACCESS_FOR_SUPER_ADMIN
          : [],
      regions: normalizeAssignedRegions(Array.isArray(data.regions) ? data.regions : []),
      active: data.active === true,
      inviteStatus: data.inviteStatus || 'active',
      createdAt: data.createdAt || null,
      updatedAt: data.updatedAt || null,
      createdByUid: data.createdByUid || ''
    };
  }

  private getSortTime(value?: Timestamp | Date | null): number {
    if (!value) {
      return 0;
    }

    if (value instanceof Date) {
      return value.getTime();
    }

    if (typeof (value as Timestamp)?.toDate === 'function') {
      return (value as Timestamp).toDate().getTime();
    }

    return 0;
  }

  private normalizeEmail(email: string): string {
    return email.trim().toLowerCase();
  }

  private async findAdminByEmail(email: string): Promise<OrganizationMember | null> {
    if (!db) {
      return null;
    }

    const snapshot = await getDocs(
      query(collection(db, 'admins'), where('email', '==', email), limit(1))
    );

    if (snapshot.empty) {
      return null;
    }

    return this.normalizeMember(snapshot.docs[0]);
  }

  private async findUidByEmail(email: string): Promise<{ uid: string; displayName?: string } | null> {
    const existingAdmin = await this.findAdminByEmail(email);
    if (existingAdmin?.uid) {
      return {
        uid: existingAdmin.uid,
        displayName: existingAdmin.displayName
      };
    }

    const { data, error } = await supabase
      .from('users')
      .select('uid, display_name, email')
      .eq('email', email)
      .not('uid', 'is', null)
      .limit(1)
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to look up existing user by email: ${error.message}`);
    }

    if (!data?.uid) {
      return null;
    }

    return {
      uid: data.uid,
      displayName: data.display_name || ''
    };
  }

  private async upsertOrganizationMember(
    uid: string,
    input: Omit<CreateOrganizationMemberInput, 'password'>,
    defaults?: Partial<OrganizationMember>
  ): Promise<OrganizationMember> {
    if (!db) {
      throw new Error('Firestore is not configured.');
    }

    const existingAdmin = await getDocs(query(collection(db, 'admins'), where('email', '==', this.normalizeEmail(input.email)), limit(1)));
    const existingData = existingAdmin.empty ? null : existingAdmin.docs[0].data();
    const preservedRole: OrganizationRole = existingData?.role === 'superAdmin' ? 'superAdmin' : input.role;
    const preservedAccess: AccessPermission[] = existingData?.role === 'superAdmin'
      ? ACCESS_FOR_SUPER_ADMIN
      : input.access;

    const memberRecord = {
      uid,
      email: this.normalizeEmail(input.email),
      displayName: input.displayName.trim() || defaults?.displayName || '',
      role: preservedRole,
      access: preservedAccess,
      regions: normalizeAssignedRegions(Array.isArray(input.regions) ? input.regions : []),
      active: true,
      inviteStatus: 'active' as const,
      createdByUid: input.createdByUid,
      createdAt: existingData?.createdAt || serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(doc(db, 'admins', uid), memberRecord, { merge: true });

    return {
      ...memberRecord,
      createdAt: defaults?.createdAt || existingData?.createdAt || new Date(),
      updatedAt: new Date()
    };
  }

  async getOrganizationMembers(): Promise<OrganizationMember[]> {
    if (!db) {
      throw new Error('Firestore is not configured.');
    }

    const snapshot = await getDocs(query(collection(db, 'admins')));

    return snapshot.docs
      .map((record) => this.normalizeMember(record))
      .sort((left, right) => {
        if (left.role === 'superAdmin' && right.role !== 'superAdmin') {
          return -1;
        }

        if (right.role === 'superAdmin' && left.role !== 'superAdmin') {
          return 1;
        }

        return this.getSortTime(right.createdAt || null) - this.getSortTime(left.createdAt || null);
      });
  }

  async createOrganizationMember(input: CreateOrganizationMemberInput): Promise<OrganizationMember> {
    if (!db) {
      throw new Error('Firestore is not configured.');
    }

    const normalizedInput = {
      ...input,
      email: this.normalizeEmail(input.email),
      displayName: input.displayName.trim()
    };
    const secondaryApp = initializeApp(firebaseConfig, `organization-manager-${Date.now()}`);
    const secondaryAuth = getAuth(secondaryApp);

    try {
      const credential = await createUserWithEmailAndPassword(secondaryAuth, normalizedInput.email, input.password);
      const uid = credential.user.uid;
      const memberRecord = await this.upsertOrganizationMember(uid, normalizedInput);
      await signOut(secondaryAuth);

      return {
        ...memberRecord,
        provisioningMode: 'created'
      };
    } catch (error: any) {
      if (error?.code === 'auth/email-already-in-use') {
        const existingUser = await this.findUidByEmail(normalizedInput.email);

        if (!existingUser?.uid) {
          throw new Error('Email already exists in Firebase Auth, but no matching app user record was found to upgrade.');
        }

        const upgradedMember = await this.upsertOrganizationMember(existingUser.uid, normalizedInput, {
          uid: existingUser.uid,
          email: normalizedInput.email,
          displayName: existingUser.displayName || normalizedInput.displayName
        });

        return {
          ...upgradedMember,
          provisioningMode: 'upgraded'
        };
      }

      throw new Error(error?.message || 'Failed to create organization member.');
    } finally {
      await deleteApp(secondaryApp);
    }
  }

  async updateOrganizationMember(
    uid: string,
    updates: Partial<Pick<OrganizationMember, 'displayName' | 'role' | 'access' | 'regions' | 'active' | 'inviteStatus'>>
  ): Promise<void> {
    if (!db) {
      throw new Error('Firestore is not configured.');
    }

    await updateDoc(doc(db, 'admins', uid), {
      ...updates,
      ...(updates.regions ? { regions: normalizeAssignedRegions(updates.regions) } : {}),
      updatedAt: serverTimestamp()
    });
  }
}

export const organizationManagementService = new OrganizationManagementService();

const ACCESS_FOR_SUPER_ADMIN: AccessPermission[] = [
  'dashboard',
  'users',
  'emails',
  'attendance',
  'events',
  'videos',
  'connections',
  'announcements',
  'news',
  'reports',
  'organization_management'
];
